import {State, Action, Selector, StateContext} from '@ngxs/store';
import {
  CheckSession,
  DeleteUser,
  EditUser,
  GetUser,
  LoginFailure,
  LoginRedirect,
  LoginSuccess,
  Logout,
  LogoutSuccess,
  RefreshToken,
  SetInit,
  GoogleLogin,
  UpdateUserPreference,
  LinkAcctToGoogle
} from '@shared/state/auth/auth.actions';
import {LoggerService} from '@app/service/logger/logger.service';
import {RoutingService} from '@app/service/routing/routing.service';
import {AuthService} from '@shared/service/auth/auth.service';
import {ToastService} from '@app/service/util/toast.service';
import {authError, ProviderData} from '@shared/interfaces/auth';
import {MessagingService} from '@shared/service/messaging/messaging.service';
import {take, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {ServerResponse, UserProfile} from '@shared/interfaces';
import {StorageService} from '@app/service/util/storage.service';
import {PROVIDER_DATA_KEY} from '../../helpers';
import {Injectable} from '@angular/core';
import {StateResetAll} from 'ngxs-reset-plugin';
import moment from 'moment';
import {User, UserCredential} from '@angular/fire/auth';
import {DocumentData, DocumentSnapshot} from 'rxfire/firestore/interfaces';

export interface AuthStateModel {
  userData: Partial<UserProfile>;
  tokenId: string;
  userId: string;
  initialized: boolean;
  error: any;
  redirectUrl: string;
  providerData: ProviderData[];
}

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    userData: {},
    tokenId: '',
    userId: '',
    initialized: false,
    error: null,
    redirectUrl: '',
    providerData: []
  }
})
@Injectable()
export class AuthState {
  ngUnsubscribe = new Subject();
  isActionLogin = false;
  firstRun = true;

  constructor(private logger: LoggerService, private authService: AuthService, private routingService: RoutingService,
              private toastService: ToastService, private msgService: MessagingService,
              private storageService: StorageService) {
  }

  @Selector()
  static getUserData(state: AuthStateModel) {
    return state.userData;
  }

  @Selector()
  static getError(state: AuthStateModel) {
    return state.error;
  }

  @Selector()
  static getUserID(state: AuthStateModel) {
    return state.userId;
  }

  @Selector()
  static getUserToken(state: AuthStateModel) {
    return state.tokenId;
  }

  @Selector()
  static getInitialized(state: AuthStateModel): boolean {
    return state.initialized;
  }

  @Selector()
  static getProviderData(state: AuthStateModel): any[] {
    return state.providerData;
  }

  @Action(CheckSession)
  checkSession(sc: StateContext<AuthStateModel>, {payload}: CheckSession) {
    return this.authService.getAuthState()
      .pipe(takeUntil(this.ngUnsubscribe), take(1))
      .subscribe({
        next: async (user: User) => {
          if (user) {
            if (payload && payload.url) {
              sc.patchState({
                redirectUrl: payload.url
              });
            }
            return sc.dispatch(new LoginSuccess(user));
          }
          sc.dispatch(new LoginFailure({message: 'silent'}));
          if (payload && payload.canNavigate) {
            return this.routingService.navigateLogin();
          }
          return null;
        },
        error: (err) => {
          this.logger.error('Check Session Err', err);
          this.routingService.navigateLogin();
        }
      });
  }

  @Action(GetUser)
  getUserFromDB(sc: StateContext<AuthStateModel>, {payload}: GetUser): any {
    const userId = sc.getState().userId;
    return this.authService.getUserData(userId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (result: DocumentSnapshot<DocumentData>) => {
          const userData = result.data();
          if (!result || !userData) {
            return sc.patchState({
              initialized: true
            });
          }
          sc.patchState({
            userData,
            initialized: true
          });
          if (this.isActionLogin || sc.getState().redirectUrl) {
            this.goToPath(sc);
            this.isActionLogin = false;
          }
        },
        error: (err) => {
          this.toastService.error(err);
          return sc.patchState({
            initialized: true
          });
        }
      });
  }

  @Action(SetInit)
  setInitialized(sc: StateContext<AuthStateModel>, {payload}: SetInit): any {
    // Do a silent check session
    sc.dispatch(new CheckSession(payload));
  }

  /**
   * Redirect the user to the page they requested after the User data is added
   * @param sc
   */
  goToPath(sc: StateContext<AuthStateModel>) {
    const redirectUrl = sc.getState().redirectUrl;
    if (redirectUrl && (redirectUrl.includes('login') || redirectUrl.includes('register') || redirectUrl.includes('forgot'))) {
      return sc.patchState({
        redirectUrl: null
      });
    }
    sc.patchState({
      redirectUrl: null
    });
    return this.routingService.decodeAndNavigateToPath(redirectUrl);
  }

  @Action(Logout)
  logout(sc: StateContext<AuthStateModel>) {
    this.ngUnsubscribe.next(0);
    this.ngUnsubscribe.complete();
    this.authService.signOut().then(
      async () => {
        await this.msgService.deleteToken();
        return sc.dispatch(new LogoutSuccess());
      });
  }

  @Action(LoginSuccess)
  async setUserStateOnSuccess(sc: StateContext<AuthStateModel>, {payload}: LoginSuccess) {
    let providerData: ProviderData[] = payload.providerData;
    sc.patchState({
      tokenId: await payload.getIdToken(true),
      userId: payload.uid,
      providerData
    });
    this.storageService.saveValue(PROVIDER_DATA_KEY, providerData);
    if (this.firstRun) {
      this.firstRun = false;
      sc.dispatch(new GetUser(payload));
    } else {
      sc.patchState({
        initialized: true
      });
      if (this.isActionLogin || sc.getState().redirectUrl) {
        this.goToPath(sc);
        this.isActionLogin = false;
      }
    }
  }

  @Action(LoginRedirect)
  redirectLogin(sc: StateContext<AuthStateModel>, {redirectUrl}: LoginRedirect) {
    if (redirectUrl) {
      sc.patchState({
        redirectUrl
      });
    }
    return this.routingService.navigateLogin();
  }

  @Action(LogoutSuccess)
  setUserStateOnLogout(sc: StateContext<AuthStateModel>) {
    sc.dispatch(new StateResetAll());
    this.storageService.clear(PROVIDER_DATA_KEY);
    sc.patchState({
      initialized: true
    });
    location.reload();
  }

  @Action(LoginFailure)
  setUserStateOnFailure(sc: StateContext<AuthStateModel>, {payload}: LoginFailure) {
    this.storageService.clear(PROVIDER_DATA_KEY);
    if (payload && (payload.data || payload.code)) {
      const message = authError(payload.data || payload);
      if (message && (message !== 'Oops, error!' && message.includes('insufficient permissions'))) {
        this.toastService.error(message);
      }
      sc.patchState({
        error: {
          message,
          code: payload.code
        },
        initialized: true
      });
    } else {
      sc.patchState({
        initialized: true
      });
    }
  }

  @Action(LinkAcctToGoogle)
  _linkAcctToGoogle(sc: StateContext<AuthStateModel>, {payload}: LinkAcctToGoogle) {
    return this.authService.linkAcctToGoogle(payload.linkType)
      .then(() => {
        sc.dispatch(new GetUser({}));
        if (payload.redirectLink) {
          this.routingService.decodeAndNavigateToPath(payload.redirectLink);
        } else {
          this.routingService.navigateHome();
        }
      })
      .then(() => {
        // Reloading so that data like reports can be in the correct area
        return window.location.reload();
      })
      .catch((error) => {
        sc.dispatch(new LoginFailure({
          data: error
        }));
      });
  }

  @Action(GoogleLogin)
  _loginWithGoogle(sc: StateContext<AuthStateModel>, {payload}: GoogleLogin) {
    return this.authService.signInWithGoogle()
      .then(async (user: UserCredential) => {
        this.isActionLogin = true;
        sc.patchState({
          redirectUrl: payload.redirectLink || ''
        });
        await this.handleSocialLogin(user);

        return sc.dispatch(new LoginSuccess(user.user));
      })
      .catch(error => {
        sc.dispatch(new LoginFailure(error));
        return this.handleSocialLoginError(error);
      });
  }

  async handleSocialLoginError(error: any) {
    if (!error || !error.code) {
      return;
    }
    if (error.code && error.code === 'auth/account-exists-with-different-credential') {
      const email = error.customData.email;
      const methods = await this.authService.fetchSignInMethods(email);
      const recMethod = methods[0];
      if (recMethod === 'password') {
        this.toastService.info('Please provide your email and password instead');
      } else if (recMethod === 'google.com') {
        this.toastService.info('Please use the google login method instead');
      }
    }
  }

  @Action(EditUser)
  editUser({getState, patchState}: StateContext<AuthStateModel>, {payload}: EditUser) {
    return this.authService.editUserData(getState().userData, payload.userData)
      .then(async (response: ServerResponse) => {
        this.toastService.response(response.type, response.message);
        patchState({
          userData: {
            ...getState().userData,
            ...response.data.userData
          }
        });
      })
      .catch((error) => {
        this.toastService.error(error.message);
      });
  }

  @Action(RefreshToken)
  async refreshToken(sc: StateContext<AuthStateModel>) {
    const currentUser = await this.authService.currentUser();
    return currentUser.getIdToken(true)
      .then(async (tokenId) => {
        sc.patchState({
          tokenId
        });
        return tokenId;
      }).catch((error) => {
        this.logger.error(error);
      });
  }

  @Action(UpdateUserPreference)
  updateUserPref({getState, setState, patchState}: StateContext<AuthStateModel>, {payload}: UpdateUserPreference) {
    const userId = getState().userId;
    return this.authService.editUserPreference(userId, payload.key, payload.value)
      .then(() => {
        const userData = getState().userData;
        patchState({
          userData: {
            ...userData,
            [payload.key]: payload.value
          }
        });
      })
      .catch(error => {
        this.toastService.error(`Unable to change your password!`, error.message);
      });
  }

  @Action(DeleteUser)
  deleteUser() {
    return this.authService.getAuthState()
      .pipe(takeUntil(this.ngUnsubscribe), take(1))
      .subscribe({
        next: async (user) => {
          if (user) {
            await user.delete();
          }
        },
        error: err => {
          this.logger.error(`Check Session Error - ${err}`);
          this.routingService.navigateLogin();
        }
      });
  }

  /**
   * This method handles social account setups from new users. It checks the creation time for a user,
   * if it was recent, an entry is created
   * @param user
   * @returns {Promise<void>}
   */
  async handleSocialLogin(user: UserCredential) {
    const creationTime = new Date(user.user.metadata.creationTime);
    const signupDate = moment(creationTime);
    const rightNow = moment();
    const duration = moment.duration(rightNow.diff(signupDate));
    const seconds = duration.asSeconds();

    // Account was created within the last minute
    if (seconds <= 120) {
      const userData: Partial<UserProfile> = {
        name: user.user.displayName,
        email: user.user.email, // If this is not included, make sure to check the multiple accounts to email setting
        userId: user.user.uid,
        photoUrl: user.user.photoURL
      };
      await this.authService.createUserEntry(userData);
    }
  }
}
